import {
  HanziScriptToggle,
  useHanziScriptToggleGate,
} from "@-/dev.improve.haniverse.components.ui.toggle.hanzi-script";
import type { HanziScript } from "@-/dev.improve.haniverse.lib.actor.types";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { useUpdateUserSettings } from "@-/dev.improve.haniverse.lib.user.settings.update";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";

/**
 * Stateful wrapper that lets the "Choose Your Script" onboarding slide
 * (`slides/000/002-hanzi-script.mdx`) render the real {@link HanziScriptToggle}.
 *
 * The toggle is a controlled component, but MDX is declarative — so the slide
 * can't own `value`/`onChange` itself. This wrapper sources the current script
 * from {@link AppLayoutContext} (the same field the Settings → Script page reads)
 * and persists the user's choice through {@link useUpdateUserSettings}, so a
 * selection made here is the real account-wide preference, not throwaway local
 * state. Unlike the Settings page there's no confirm dialog: during onboarding
 * the user hasn't studied anything yet, so switching has no progress to disrupt.
 *
 * The example comparison table lives in a sibling `HanziScriptTable` component so
 * the slide can place it independently of this control.
 */
export function SlideHanziScriptToggle() {
  const hanziScriptToggleGate = useHanziScriptToggleGate();
  const { hanziScript: script } = useActorUser();
  const updateUserSettingsMutation = useUpdateUserSettings();

  if (hanziScriptToggleGate === null) {
    return (
      <Text selectable variant="p">
        Haniverse currently teaches Simplified Chinese, the form used in Mainland China and
        Singapore.
      </Text>
    );
  }

  return (
    <>
      <Text selectable variant="p">
        Choose Simplified or Traditional Chinese. Haniverse recommends Simplified unless there is a
        specific reason to learn Traditional. This can be changed later in Settings.
      </Text>
      <HanziScriptToggle
        gate={hanziScriptToggleGate}
        onChange={(nextScript: HanziScript) => {
          if (nextScript !== script) {
            updateUserSettingsMutation.mutate({ hanziScript: nextScript });
          }
        }}
        value={script}
      />
    </>
  );
}
